import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, LineChart, Award, Calendar, Calculator, Brain, Target, Divide } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Language } from '../types';
import StudentProgress from './StudentProgress';

interface ClassroomManagementProps {
  language: Language;
}

interface Classroom {
  id: string;
  name: string;
  student_count: number;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  progress?: {
    total_exercises: number;
    correct_answers: number;
    average_accuracy: number;
    recent_improvement: number;
    badges_count: number;
    exercises_per_day: number;
  };
}

interface ClassAverages {
  exercisesPerDay: number;
  accuracy: number;
  badges: number;
  activityAccuracy: {
    multiplication: number;
    division: number;
    prime: number;
    square: number;
  };
}

const ClassroomManagement: React.FC<ClassroomManagementProps> = ({ language }) => {
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<string | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [classroomInput, setClassroomInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableClassrooms, setAvailableClassrooms] = useState<Classroom[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [classAverages, setClassAverages] = useState<ClassAverages>({
    exercisesPerDay: 0,
    accuracy: 0,
    badges: 0,
    activityAccuracy: {
      multiplication: 0,
      division: 0,
      prime: 0,
      square: 0
    }
  });

  useEffect(() => {
    loadClassrooms();
    loadAvailableClassrooms();
  }, []);

  useEffect(() => {
    if (selectedClassroom) {
      loadStudents(selectedClassroom);
    }
  }, [selectedClassroom]);

  useEffect(() => {
    if (students.length > 0) {
      calculateClassAverages();
    }
  }, [students]);

  const calculateClassAverages = async () => {
    try {
      const studentsWithProgress = students.filter(s => s.progress);
      if (studentsWithProgress.length === 0) return;

      // Calculate basic averages
      const averages = {
        exercisesPerDay: Math.round(
          studentsWithProgress.reduce((sum, s) => sum + (s.progress?.exercises_per_day || 0), 0) / 
          studentsWithProgress.length
        ),
        accuracy: Math.round(
          studentsWithProgress.reduce((sum, s) => sum + (s.progress?.average_accuracy || 0), 0) / 
          studentsWithProgress.length
        ),
        badges: Math.round(
          studentsWithProgress.reduce((sum, s) => sum + (s.progress?.badges_count || 0), 0) / 
          studentsWithProgress.length
        ),
        activityAccuracy: {
          multiplication: 0,
          division: 0,
          prime: 0,
          square: 0
        }
      };

      // Get activity-specific data
      const activities = ['multiplication', 'division', 'prime', 'square'] as const;
      
      for (const activity of activities) {
        const { data } = await supabase
          .from('user_progress')
          .select('correct_answers, questions_answered')
          .eq('operation', activity)
          .in('user_id', studentsWithProgress.map(s => s.id));

        if (data && data.length > 0) {
          const totalCorrect = data.reduce((sum, d) => sum + d.correct_answers, 0);
          const totalQuestions = data.reduce((sum, d) => sum + d.questions_answered, 0);
          averages.activityAccuracy[activity] = totalQuestions > 0 
            ? Math.round((totalCorrect / totalQuestions) * 100)
            : 0;
        }
      }

      setClassAverages(averages);
    } catch (error) {
      console.error('Error calculating class averages:', error);
    }
  };

  const loadClassrooms = async () => {
    try {
      const { data: classroomsData, error: classroomsError } = await supabase
        .from('classrooms')
        .select(`
          id,
          name,
          student_count:classroom_students(count)
        `)
        .returns<{ id: string; name: string; student_count: { count: number }[] }[]>();

      if (classroomsError) throw classroomsError;

      const transformedClassrooms: Classroom[] = (classroomsData || []).map(classroom => ({
        id: classroom.id,
        name: classroom.name,
        student_count: classroom.student_count?.[0]?.count || 0
      }));

      setClassrooms(transformedClassrooms);
    } catch (error) {
      console.error('Error loading classrooms:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableClassrooms = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: availableClassroomsData, error: availableClassroomsError } = await supabase
        .from('classrooms')
        .select('id, name')
        .neq('teacher_id', user.id)
        .order('name');

      if (availableClassroomsError) throw availableClassroomsError;
      setAvailableClassrooms(availableClassroomsData || []);
    } catch (error) {
      console.error('Error loading available classrooms:', error);
    }
  };

  const loadStudents = async (classroomId: string) => {
    try {
      const { data: studentData, error: studentError } = await supabase
        .from('classroom_students')
        .select(`
          profiles!classroom_students_student_id_fkey (
            id,
            first_name,
            last_name
          )
        `)
        .eq('classroom_id', classroomId);

      if (studentError) throw studentError;

      if (!studentData) {
        setStudents([]);
        return;
      }

      const students: Student[] = studentData
        .map(data => {
          if (!data.profiles) return null;
          
          return {
            id: data.profiles.id,
            first_name: data.profiles.first_name,
            last_name: data.profiles.last_name
          };
        })
        .filter((student): student is Student => student !== null);

      for (const student of students) {
        const { data: progressData } = await supabase
          .from('user_progress')
          .select('questions_answered, correct_answers, created_at')
          .eq('user_id', student.id);

        const { count: badgesCount } = await supabase
          .from('user_badges')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', student.id);

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: recentProgress } = await supabase
          .from('user_progress')
          .select('questions_answered, correct_answers')
          .eq('user_id', student.id)
          .gte('created_at', thirtyDaysAgo.toISOString());

        if (progressData && progressData.length > 0) {
          const totalExercises = progressData.reduce((sum, p) => sum + p.questions_answered, 0);
          const correctAnswers = progressData.reduce((sum, p) => sum + p.correct_answers, 0);
          
          // Calculate exercises per day
          const dates = new Set(progressData.map(p => new Date(p.created_at).toDateString()));
          const daysWithExercises = dates.size;
          const exercisesPerDay = daysWithExercises > 0 ? Math.round(totalExercises / daysWithExercises) : 0;

          let improvement = 0;
          if (recentProgress && recentProgress.length > 0) {
            const recentTotal = recentProgress.reduce((sum, p) => sum + p.questions_answered, 0);
            const recentCorrect = recentProgress.reduce((sum, p) => sum + p.correct_answers, 0);
            const recentAccuracy = recentTotal > 0 ? (recentCorrect / recentTotal) * 100 : 0;
            const overallAccuracy = totalExercises > 0 ? (correctAnswers / totalExercises) * 100 : 0;
            improvement = recentAccuracy - overallAccuracy;
          }

          student.progress = {
            total_exercises: totalExercises,
            correct_answers: correctAnswers,
            average_accuracy: totalExercises > 0 ? Math.round((correctAnswers / totalExercises) * 100) : 0,
            recent_improvement: Math.round(improvement),
            badges_count: badgesCount || 0,
            exercises_per_day: exercisesPerDay
          };
        } else {
          student.progress = {
            total_exercises: 0,
            correct_answers: 0,
            average_accuracy: 0,
            recent_improvement: 0,
            badges_count: 0,
            exercises_per_day: 0
          };
        }
      }

      setStudents(students);
    } catch (error) {
      console.error('Error loading students:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const handleClassroomAction = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('No authenticated user');

      const existingClassroom = availableClassrooms
        .find(c => c.name.toLowerCase() === classroomInput.toLowerCase());

      if (existingClassroom) {
        const { error } = await supabase
          .from('classrooms')
          .update({ teacher_id: user.id })
          .eq('id', existingClassroom.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('classrooms')
          .insert({ 
            name: classroomInput,
            teacher_id: user.id
          });

        if (error) throw error;
      }

      setClassroomInput('');
      loadClassrooms();
      loadAvailableClassrooms();
    } catch (error) {
      console.error('Error with classroom action:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const deleteClassroom = async (classroomId: string) => {
    try {
      const { error } = await supabase
        .from('classrooms')
        .delete()
        .eq('id', classroomId);

      if (error) throw error;

      setSelectedClassroom(null);
      loadClassrooms();
      loadAvailableClassrooms();
    } catch (error) {
      console.error('Error deleting classroom:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600 dark:text-slate-300">
          {language === 'en' ? 'Loading...' : 'Chargement...'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Your Classrooms' : 'Vos Classes'}
          </h2>

          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={classroomInput}
                  onChange={(e) => setClassroomInput(e.target.value)}
                  placeholder={language === 'en' ? 'Create or select classroom' : 'Créer ou sélectionner une classe'}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 
                    text-slate-900 dark:text-slate-100 rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  list="available-classrooms"
                  autoComplete="off"
                />
                <datalist id="available-classrooms">
                  {availableClassrooms
                    .filter(c => !classrooms.some(oc => oc.id === c.id))
                    .map(classroom => (
                      <option key={classroom.id} value={classroom.name} />
                    ))
                  }
                </datalist>
              </div>
              <button
                onClick={handleClassroomAction}
                disabled={!classroomInput}
                className="p-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
                  hover:bg-indigo-700 dark:hover:bg-indigo-600
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2 mt-4">
              {classrooms.map((classroom) => (
                <div
                  key={classroom.id}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors
                    ${selectedClassroom === classroom.id
                      ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                      : 'bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                >
                  <button
                    onClick={() => setSelectedClassroom(classroom.id)}
                    className="flex-1 flex items-center justify-between"
                  >
                    <span className="font-medium">{classroom.name}</span>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4" />
                      <span>{classroom.student_count}</span>
                    </div>
                  </button>
                  <button
                    onClick={() => deleteClassroom(classroom.id)}
                    className="ml-2 p-1 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {classrooms.length === 0 && (
                <div className="text-center py-4 text-slate-600 dark:text-slate-400">
                  {language === 'en' 
                    ? 'No classrooms yet. Create your first classroom!'
                    : 'Pas encore de classes. Créez votre première classe !'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="space-y-6">
            {/* Class Statistics */}
            {selectedClassroom && students.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
                  {language === 'en' ? 'Class Statistics' : 'Statistiques de la classe'}
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg text-center">
                    <Calendar className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.exercisesPerDay}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Exercises per day' : 'Exercices par jour'}
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg text-center">
                    <LineChart className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.accuracy}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Average accuracy' : 'Précision moyenne'}
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg text-center">
                    <Award className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.badges}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Badges per student' : 'Badges par élève'}
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  {language === 'en' ? 'Activity Performance' : 'Performance par activité'}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                    <Calculator className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mb-2" />
                    <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.activityAccuracy.multiplication}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Multiplication' : 'Multiplication'}
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                    <Divide className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mb-2" />
                    <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.activityAccuracy.division}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Division' : 'Division'}
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                    <Brain className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mb-2" />
                    <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.activityAccuracy.prime}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Prime Numbers' : 'Nombres premiers'}
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                    <Target className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mb-2" />
                    <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      {classAverages.activityAccuracy.square}%
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Square Numbers' : 'Nombres carrés'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Students Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
              {selectedClassroom ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
                      {language === 'en' ? 'Students' : 'Élèves'}
                    </h2>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-slate-600 dark:text-slate-300">
                            {language === 'en' ? 'Name' : 'Nom'}
                          </th>
                          <th className="text-center py-3 px-4 text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {language === 'en' ? 'Exercises/Day' : 'Exercices/Jour'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {language === 'en' ? `Class avg: ${classAverages.exercisesPerDay}` : `Moyenne: ${classAverages.exercisesPerDay}`}
                            </div>
                          </th>
                          <th className="text-center py-3 px-4 text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-center gap-1">
                              <LineChart className="h-4 w-4" />
                              {language === 'en' ? 'Progress' : 'Progrès'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {language === 'en' ? `Class avg: ${classAverages.accuracy}%` : `Moyenne: ${classAverages.accuracy}%`}
                            </div>
                          </th>
                          <th className="text-center py-3 px-4 text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-center gap-1">
                              <Award className="h-4 w-4" />
                              {language === 'en' ? 'Badges' : 'Badges'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {language === 'en' ? `Class avg: ${classAverages.badges}` : `Moyenne: ${classAverages.badges}`}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student) => (
                          <tr 
                            key={student.id} 
                            className={`border-b border-slate-100 dark:border-slate-700 cursor-pointer 
                              hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors
                              ${selectedStudent === student.id ? 'bg-indigo-50 dark:bg-indigo-900/50' : ''}`}
                            onClick={() => setSelectedStudent(student.id)}
                          >
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-slate-800 dark:text-slate-100">
                                  {student.first_name} {student.last_name}
                                </div>
                                {student.progress?.recent_improvement !== 0 && (
                                  <div className={`text-sm ${
                                    student.progress?.recent_improvement > 0 
                                      ? 'text-green-600 dark:text-green-400' 
                                      : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {student.progress?.recent_improvement > 0 ? '+' : ''}
                                    {student.progress?.recent_improvement}%
                                    {language === 'en' ? ' last 30 days' : ' 30 derniers jours'}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="font-medium text-slate-700 dark:text-slate-300">
                                {student.progress?.exercises_per_day || 0}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex flex-col items-center">
                                <div className="font-medium text-slate-700 dark:text-slate-300">
                                  {student.progress?.average_accuracy || 0}%
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  {student.progress?.total_exercises || 0} {language === 'en' ? 'exercises' : 'exercices'}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="font-medium text-slate-700 dark:text-slate-300">
                                {student.progress?.badges_count || 0}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {students.length === 0 && (
                      <div className="text-center py-8 text-slate-600 dark:text-slate-400">
                        {language === 'en' 
                          ? 'No students in this classroom yet'
                          : 'Pas encore d\'élèves dans cette classe'}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                  {language === 'en' 
                    ? 'Select a classroom to view students'
                    : 'Sélectionnez une classe pour voir les élèves'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedStudent && (
        <StudentProgress
          studentId={selectedStudent}
          language={language}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default ClassroomManagement;